<script lang="ts">
  import { getContext } from 'svelte';
  import { TwistBox } from '@twist/core';
  import { type SanityContext, key } from '$lib/context/sanity';
  import type { Media } from '$lib/sanity/schemas';
  import { pipe } from '$lib/utils/pipe';
  import {
    toImageSrcAttributes,
    toLazyloadSrcsetUrlBuilders,
    toSrcsetUrlBuilders,
    useSanityImageLazyload
  } from '$lib/sanity/image';
  import { extractAspectRatioFromAsset } from '$lib/utils/sanity';
  import { toCSSVars } from '$lib/utils/cssVars';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import PortableText from '$components/SanityComponents/PortableText/PortableText.svelte';
  import Video from '$components/SanityComponents/Common/Video.svelte';

  interface Props {
    document: Media;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  const { urlBuilder } = getContext<SanityContext>(key);
  const { isInView, lazyload } = useSanityImageLazyload();

  let srcAttrs = $derived(
    document?.image?.asset &&
      pipe(
        document.image.asset,
        toSrcsetUrlBuilders(urlBuilder),
        toLazyloadSrcsetUrlBuilders($isInView),
        toImageSrcAttributes()
      )
  );
  let aspectRatio = $derived(document.image && extractAspectRatioFromAsset(document.image));
  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));
</script>

<div>
  {#if document.type === 'image' && document.image && srcAttrs}
    <img
      use:lazyload
      class="media-image"
      srcset={srcAttrs.srcset}
      src={srcAttrs.src}
      alt={document?.image?.asset.altText || ''}
      style={toCSSVars({ aspectRatio })}
      data-sanity={encodeDataAttribute([...path, 'imageRef'])}
    />
  {/if}

  {#if document.type === 'video' && document.video}
    <Video {sourceMap} {path} document={document.video} />
  {/if}

  {#if document.caption?.copy}
    <TwistBox marginTop={toFluidVarInput(12, 12)}>
      <div
        class="media-caption-container"
        style={toCSSVars({
          captionAlignment: document.caption.alignment
        })}
      >
        <span
          class="media-caption"
          style={toCSSVars({
            captionWidth: document.caption.width
          })}
        >
          <PortableText
            {sourceMap}
            body={document.caption.copy}
            path={[...path, 'caption', 'copy', 'body']}
          />
        </span>
      </div>
    </TwistBox>
  {/if}
</div>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  .media-image {
    aspect-ratio: var(--aspect-ratio);
    border-radius: var(--theme-general-image-border-radius, none);
    height: auto;
    width: 100%;
  }

  .media-caption-container {
    display: flex;
    justify-content: var(--caption-alignment);
    width: 100%;

    .media-caption {
      text-align: var(--caption-alignment);
      width: 100%;

      @include at('lg') {
        width: var(--caption-width);
      }
    }
  }
</style>
